import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock, CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { assessmentApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function AssessmentView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [assessment, setAssessment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attemptId, setAttemptId] = useState(null);
  const [responses, setResponses] = useState({});
  const [result, setResult] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [showSectionNav, setShowSectionNav] = useState(false);
  const timerRef = useRef(null);
  const topRef = useRef(null);

  useEffect(() => {
    loadAssessment();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [id]);

  const loadAssessment = async () => {
    try {
      const res = await assessmentApi.getAssessment(id);
      setAssessment(res.data);
    } catch (error) {
      toast.error('Failed to load assessment');
      navigate(-1);
    } finally {
      setIsLoading(false);
    }
  };

  // Group questions by section (parsed from [Section] prefix)
  const sections = useMemo(() => {
    if (!assessment?.questions) return [];
    const sectionMap = new Map();
    assessment.questions.forEach(q => {
      const match = q.question_text.match(/^\[([^\]]+)\]\s*/);
      const sectionName = match ? match[1] : 'General';
      const cleanText = match ? q.question_text.replace(match[0], '') : q.question_text;
      if (!sectionMap.has(sectionName)) {
        sectionMap.set(sectionName, []);
      }
      sectionMap.get(sectionName).push({ ...q, cleanText });
    });
    return Array.from(sectionMap.entries()).map(([name, questions]) => ({ name, questions }));
  }, [assessment?.questions]);

  const hasSections = sections.length > 1;
  const currentQuestions = hasSections ? (sections[currentSection]?.questions || []) : (assessment?.questions || []);

  const handleStartAttempt = async () => {
    try {
      const res = await assessmentApi.startAttempt(id);
      setAttemptId(res.data.attemptId);

      if (assessment.time_limit_minutes) {
        setTimeLeft(assessment.time_limit_minutes * 60);
        timerRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              clearInterval(timerRef.current);
              handleSubmit();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start attempt');
    }
  };

  const handleResponseChange = (questionId, value, type) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: type === 'mcq'
        ? { ...prev[questionId], selectedOption: value }
        : { ...prev[questionId], answerText: value }
    }));
  };

  const handleSubmit = async () => {
    if (!attemptId) return;
    setIsSubmitting(true);

    try {
      const formattedResponses = Object.entries(responses).map(([questionId, data]) => ({
        questionId,
        selectedOption: data.selectedOption || null,
        answerText: data.answerText || null
      }));

      const res = await assessmentApi.submitAttempt(attemptId, formattedResponses);
      setResult(res.data);
      if (timerRef.current) clearInterval(timerRef.current);

      const attemptRes = await assessmentApi.getAttempt(attemptId);
      setResult(prev => ({ ...prev, attempt: attemptRes.data }));
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const goToSection = (idx) => {
    setCurrentSection(idx);
    setShowSectionNav(false);
    topRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getSectionProgress = (sectionQuestions) => {
    const answered = sectionQuestions.filter(q => responses[q.id]?.selectedOption || responses[q.id]?.answerText).length;
    return { answered, total: sectionQuestions.length };
  };

  const getTotalProgress = () => {
    const allQ = sections.flatMap(s => s.questions);
    const answered = allQ.filter(q => responses[q.id]?.selectedOption || responses[q.id]?.answerText).length;
    return { answered, total: allQ.length };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!assessment) return null;

  // Results view
  if (result) {
    // Group results by section
    const resultSections = [];
    if (result.attempt?.responses) {
      const sectionMap = new Map();
      result.attempt.responses.forEach(r => {
        const match = r.question_text.match(/^\[([^\]]+)\]\s*/);
        const sectionName = match ? match[1] : 'General';
        const cleanText = match ? r.question_text.replace(match[0], '') : r.question_text;
        if (!sectionMap.has(sectionName)) sectionMap.set(sectionName, []);
        sectionMap.get(sectionName).push({ ...r, cleanText });
      });
      sectionMap.forEach((questions, name) => resultSections.push({ name, questions }));
    }

    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{assessment.title}</h2>
          <p className="text-gray-500 mb-6">{result.message}</p>

          {result.score !== null && result.score !== undefined && (
            <div className="mb-6">
              <div className="text-5xl font-bold text-primary-600">{result.percentage}%</div>
              <p className="text-gray-500 mt-1">{result.score} / {result.maxScore} points</p>
            </div>
          )}

          {/* Section-wise score summary */}
          {resultSections.length > 1 && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-6 text-left">
              {resultSections.map((sec, i) => {
                const correct = sec.questions.filter(q => q.is_correct === true).length;
                return (
                  <div key={i} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-500 truncate">{sec.name}</p>
                    <p className="text-lg font-bold text-gray-900">{correct}/{sec.questions.length}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Question-by-question results grouped by section */}
        {resultSections.map((sec, si) => (
          <div key={si} className="mb-6">
            {resultSections.length > 1 && (
              <h3 className="font-semibold text-gray-900 mb-3 text-lg">{sec.name}</h3>
            )}
            <div className="space-y-3">
              {sec.questions.map((r, i) => (
                <div key={i} className={`p-4 rounded-lg border ${
                  r.is_correct === true ? 'border-green-200 bg-green-50' :
                  r.is_correct === false ? 'border-red-200 bg-red-50' :
                  'border-gray-200 bg-gray-50'
                }`}>
                  <div className="flex items-start">
                    {r.is_correct === true ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : r.is_correct === false ? (
                      <XCircle className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{r.cleanText || r.question_text}</p>
                      {r.question_type === 'mcq' && (
                        <p className="text-sm text-gray-600 mt-1">
                          Your answer: {r.selected_option || 'Not answered'}
                          {r.correct_answer && ` | Correct: ${r.correct_answer}`}
                        </p>
                      )}
                      {r.explanation && (
                        <p className="text-sm text-primary-600 mt-1">{r.explanation}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Pre-start view
  if (!attemptId) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 mb-4">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>

        <div className="bg-white border border-gray-200 rounded-xl p-8">
          <h1 className="text-2xl font-bold text-gray-900">{assessment.title}</h1>
          {assessment.description && (
            <p className="text-gray-600 mt-2">{assessment.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Questions</p>
              <p className="font-semibold">{assessment.questions?.length || 0}</p>
            </div>
            {assessment.time_limit_minutes && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Time Limit</p>
                <p className="font-semibold">{assessment.time_limit_minutes} minutes</p>
              </div>
            )}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-gray-500">Max Attempts</p>
              <p className="font-semibold">
                {assessment.max_attempts > 0 ? `${assessment.attemptCount || 0}/${assessment.max_attempts}` : 'Unlimited'}
              </p>
            </div>
            {hasSections && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-gray-500">Subjects</p>
                <p className="font-semibold">{sections.length} sections</p>
              </div>
            )}
          </div>

          {/* Section breakdown */}
          {hasSections && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-medium text-gray-700">Section Breakdown:</p>
              {sections.map((sec, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                  <span className="font-medium text-gray-700">{sec.name}</span>
                  <span className="text-gray-500">{sec.questions.length} questions</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={handleStartAttempt}
            className="mt-6 w-full py-3 bg-primary-600 text-white rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  // Active quiz view - section by section
  const totalProgress = hasSections ? getTotalProgress() : null;

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto" ref={topRef}>
      {/* Top bar: title + timer */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900 truncate">{assessment.title}</h1>
        <div className="flex items-center gap-3 flex-shrink-0">
          {totalProgress && (
            <span className="text-xs text-gray-500 hidden sm:inline">
              {totalProgress.answered}/{totalProgress.total} answered
            </span>
          )}
          {timeLeft !== null && (
            <div className={`flex items-center px-3 py-1.5 rounded-lg text-sm font-medium ${
              timeLeft < 300 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}>
              <Clock className="w-4 h-4 mr-1" />
              {formatTime(timeLeft)}
            </div>
          )}
        </div>
      </div>

      {/* Section navigation bar */}
      {hasSections && (
        <div className="mb-4">
          {/* Section tabs - horizontal scroll on mobile */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {sections.map((sec, i) => {
              const prog = getSectionProgress(sec.questions);
              const isActive = i === currentSection;
              return (
                <button
                  key={i}
                  onClick={() => goToSection(i)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                    isActive
                      ? 'bg-primary-600 text-white shadow-sm'
                      : prog.answered === prog.total && prog.total > 0
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <span>{sec.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20' : 'bg-gray-100'
                  }`}>
                    {prog.answered}/{prog.total}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Current section header */}
      {hasSections && (
        <div className="flex items-center justify-between mb-4 px-1">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              Section {currentSection + 1}: {sections[currentSection]?.name}
            </h2>
            <p className="text-xs text-gray-500">
              {currentQuestions.length} questions
              {' · '}
              {getSectionProgress(currentQuestions).answered} answered
            </p>
          </div>
        </div>
      )}

      {/* Questions */}
      <div className="space-y-5">
        {currentQuestions.map((q, index) => {
          const displayText = q.cleanText || q.question_text;
          const globalIndex = hasSections
            ? sections.slice(0, currentSection).reduce((sum, s) => sum + s.questions.length, 0) + index
            : index;

          return (
            <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-5">
              <p className="font-medium text-gray-900 mb-3">
                <span className="text-primary-600 mr-2">Q{globalIndex + 1}.</span>
                {displayText}
              </p>

              {q.question_type === 'mcq' && q.options && (
                <div className="space-y-2">
                  {q.options.map(opt => (
                    <label
                      key={opt.key}
                      className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                        responses[q.id]?.selectedOption === opt.key
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q_${q.id}`}
                        value={opt.key}
                        checked={responses[q.id]?.selectedOption === opt.key}
                        onChange={(e) => handleResponseChange(q.id, e.target.value, 'mcq')}
                        className="mr-3"
                      />
                      <span className="font-medium text-gray-600 mr-2">{opt.key}.</span>
                      <span className="text-gray-800">{opt.text}</span>
                    </label>
                  ))}
                </div>
              )}

              {q.question_type === 'subjective' && (
                <textarea
                  value={responses[q.id]?.answerText || ''}
                  onChange={(e) => handleResponseChange(q.id, e.target.value, 'subjective')}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="Type your answer..."
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom navigation */}
      <div className="mt-6 flex items-center gap-3">
        {hasSections && currentSection > 0 && (
          <button
            onClick={() => goToSection(currentSection - 1)}
            className="flex items-center px-5 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous: {sections[currentSection - 1]?.name}
          </button>
        )}

        <div className="flex-1" />

        {hasSections && currentSection < sections.length - 1 ? (
          <button
            onClick={() => goToSection(currentSection + 1)}
            className="flex items-center px-5 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Next: {sections[currentSection + 1]?.name}
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Assessment'}
          </button>
        )}
      </div>
    </div>
  );
}

export default AssessmentView;
